import { CloseCircle } from 'iconsax-react';
import { useRef } from 'react';
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router';
import { useOnClickOutside } from 'usehooks-ts';
import ConditionalLayout from '@/components/layouts/conditional-layout';
import ROUTES from '@/routes';
import { useMessageStore } from '@/stores/message';
import cn from '@/utils/merge-class-names';
import { parseSessionKey } from '@/utils/session';

type ChatRoomLayoutProps = {
  type: 'group' | 'session';
};

type OuterVariant = 'backdrop' | 'menuCenter' | 'menuRight' | 'preview' | 'media' | 'default';

type ContentVariant =
  | 'wide'
  | 'narrow'
  | 'menu'
  | 'search'
  | 'members'
  | 'preview'
  | 'media'
  | 'applications'
  | 'default';

const OUTER_CLASSNAME: Record<OuterVariant, string> = {
  backdrop: 'w-screen h-screen py-16 left-0 top-0 flex justify-center bg-neutral-800/30',
  menuCenter: 'h-fit w-fit py-0 bg-transparent left-1/2 -translate-x-1/2 top-6',
  menuRight: 'h-fit w-fit py-0 bg-transparent right-5 top-14',
  preview: 'w-screen h-screen left-0 top-0 flex z-50 justify-center bg-neutral-800/30',
  media: 'w-screen h-screen py-14 left-0 top-0 flex justify-center bg-neutral-800/30',
  default: '',
};

const CONTENT_CLASSNAME: Record<ContentVariant, string> = {
  narrow: 'w-[400px] flex-1',
  wide: 'w-[60rem] flex-1',
  menu: 'w-[400px] max-h-[800px]',
  search: 'w-[400px] h-[600px]',
  members: 'w-[400px] h-[600px]',
  preview: 'w-screen h-screen p-0 rounded-none',
  media: 'w-[36.5rem] h-full pt-4 pb-6 px-1',
  applications: 'w-[400px] h-70vh',
  default: '',
};

export default function ChatRoomLayout({ type }: ChatRoomLayoutProps) {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const parentPath = ROUTES.MESSAGES[type === 'session' ? 'SESSIONS' : 'GROUPS'];
  const childrenPaths: Record<
    string,
    { outer: OuterVariant; content: ContentVariant; showBottomCloseButton?: boolean }
  > = {
    [parentPath.COLLECTIONS.$path()]: { outer: 'backdrop', content: 'wide' },
    [parentPath.COLLECTIONS.BY_ID.$path()]: { outer: 'preview', content: 'preview' },
    [parentPath.MEDIA.$path()]: { outer: 'media', content: 'media' },
    [parentPath.MEDIA.BY_ID.$path()]: { outer: 'preview', content: 'preview' },
    [parentPath.CONTACT.$path()]: { outer: 'backdrop', content: 'narrow', showBottomCloseButton: true },
    [parentPath.FORWARD.$path()]: { outer: 'backdrop', content: 'narrow', showBottomCloseButton: true },
    [parentPath.REPORT.$path()]: { outer: 'menuRight', content: 'narrow' },
    [parentPath.SEARCH.$path()]: { outer: 'menuRight', content: 'search' },
    [ROUTES.MESSAGES.GROUPS.SETTINGS.QR_CODE.$path()]: { outer: 'menuRight', content: 'narrow' },
    [`${ROUTES.MESSAGES.GROUPS.SETTINGS.MEMBERS.$path()}/*`]: { outer: 'menuCenter', content: 'members' },
    [ROUTES.MESSAGES.GROUPS.SETTINGS.ADMIN.INFO.$path()]: { outer: 'menuCenter', content: 'members' },
    [ROUTES.MESSAGES.GROUPS.SETTINGS.ADMIN.APPLICATIONS.$path()]: { outer: 'menuRight', content: 'applications' },
    [`${parentPath.SETTINGS.$path()}/*`]: { outer: 'menuRight', content: 'menu' },
    [parentPath.MESSAGE_BY_ID.$path()]: { outer: 'preview', content: 'preview' },
  };
  const {
    outer: modalOuterVariant,
    content: modalContentVariant,
    showBottomCloseButton,
  } = Object.entries(childrenPaths).find(([path]) => matchPath(path, location.pathname))?.[1] || {
    outer: 'default',
    content: 'default',
  };

  const closeOnClickOutside = ['menuRight', 'menuCenter', 'preview'].some((variant) => variant === modalOuterVariant);

  const { key: sessionKey } = useMessageStore();
  const id = parseSessionKey(sessionKey || '')[1];
  useOnClickOutside(ref, () => {
    // Prevent navigating back when the confirmation/flyout modal is open
    if (
      document.getElementById('headlessui-portal-root') ||
      document.getElementById('flyout-modal')?.classList.contains('flex')
    )
      return;
    navigate(parentPath.$buildPath({ params: { id } }), { replace: true });
  });

  return (
    <ConditionalLayout pathname={`${parentPath.MESSAGE_BY_ID.$path()}/*`}>
      {(isDesktop, match) => {
        return isDesktop ? (
          match && (
            <div
              id='chatroom-modal'
              className={cn('group modal-layout fixed z-40', OUTER_CLASSNAME[modalOuterVariant])}
              onClick={() => modalContentVariant !== 'narrow' && navigate(-1)}>
              <div className='flex-col flex flex-1 items-center justify-center'>
                <div
                  ref={closeOnClickOutside ? ref : undefined}
                  className={cn(
                    'flex bg-white rounded-2xl overflow-x-hidden overflow-y-auto shadow-modal relative p-4 [&_.navbar]:h-12',
                    CONTENT_CLASSNAME[modalContentVariant],
                  )}
                  onClick={(e) => e.stopPropagation()}>
                  <Outlet />
                </div>
                {showBottomCloseButton && (
                  <button
                    type='button'
                    className='btn btn-ghost btn-circle btn-md mt-2'
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(-1);
                    }}>
                    <CloseCircle
                      variant='Bold'
                      className='text-neutral-0 size-10'
                    />
                  </button>
                )}
              </div>
            </div>
          )
        ) : (
          <Outlet />
        );
      }}
    </ConditionalLayout>
  );
}
